/**
 * Hook para gerenciar refresh de sessão quando a aba volta a ficar ativa
 * 
 * Problema: Quando o usuário deixa a aba inativa por muito tempo,
 * o token pode expirar e causar erros CORS/Failed to fetch
 * 
 * Solução: Detectar quando a aba volta a ficar ativa e verificar/renovar sessão
 * com retry automático em caso de erro de rede
 */

import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const SESSION_CHECK_THRESHOLD = 5 * 60 * 1000; // 5 minutos
const DEBUG = import.meta.env.DEV;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 segundo

// Função auxiliar para retry com backoff exponencial
const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = MAX_RETRIES,
  delay: number = RETRY_DELAY
): Promise<T | null> => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Verificar se é erro de rede/DNS
      const isNetworkError = 
        lastError.message.includes('Failed to fetch') ||
        lastError.message.includes('ERR_NAME_NOT_RESOLVED') ||
        lastError.message.includes('Network') ||
        lastError.message.includes('offline');

      if (!isNetworkError || attempt === maxRetries - 1) {
        throw error;
      }

      // Aguardar antes de tentar novamente (backoff exponencial)
      const waitTime = delay * Math.pow(2, attempt);
      if (DEBUG) console.log(`[SessionRefresh] Retry attempt ${attempt + 1}/${maxRetries} after ${waitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }

  throw lastError;
};

export function useSessionRefresh() {
  const lastCheckRef = useRef<number>(Date.now());
  const isCheckingRef = useRef<boolean>(false);

  useEffect(() => {
    const checkAndRefreshSession = async () => {
      // Evitar múltiplas verificações simultâneas
      if (isCheckingRef.current) {
        if (DEBUG) console.log('[SessionRefresh] Check already in progress, skipping');
        return;
      }

      const now = Date.now();
      const timeSinceLastCheck = now - lastCheckRef.current;

      // Só verificar se passou tempo suficiente desde a última verificação
      if (timeSinceLastCheck < SESSION_CHECK_THRESHOLD) {
        if (DEBUG) console.log('[SessionRefresh] Too soon since last check, skipping');
        return;
      }

      isCheckingRef.current = true;
      lastCheckRef.current = now;

      try {
        if (DEBUG) console.log('[SessionRefresh] Checking session validity...');

        // Tentar obter a sessão atual com retry
        let session = null;
        let error = null;

        try {
          const result = await retryWithBackoff(() => supabase.auth.getSession());
          if (result) {
            session = result.data?.session || null;
            error = result.error;
          }
        } catch (retryError) {
          console.error('[SessionRefresh] Failed to get session after retries:', retryError);
          error = retryError;
        }

        if (error) {
          console.error('[SessionRefresh] Error getting session:', error);
          
          // Se houver erro ao obter sessão, tentar refresh com retry
          try {
            const refreshResult = await retryWithBackoff(() => supabase.auth.refreshSession());
            
            if (!refreshResult || !refreshResult.data?.session) {
              throw new Error('Session refresh returned no session');
            }

            if (DEBUG) console.log('[SessionRefresh] Session refreshed successfully');
            return;
          } catch (refreshError) {
            console.error('[SessionRefresh] Session refresh failed:', refreshError);
            
            // Só mostrar erro se não for erro de rede transitório
            const isNetworkError = 
              String(refreshError).includes('Failed to fetch') ||
              String(refreshError).includes('ERR_NAME_NOT_RESOLVED') ||
              String(refreshError).includes('Network');

            if (!isNetworkError) {
              toast.error('Sessão expirada', {
                description: 'Por favor, faça login novamente',
                duration: 5000,
              });
              
              await supabase.auth.signOut();
              
              setTimeout(() => {
                window.location.href = '/entrar';
              }, 1000);
            }
            return;
          }
        }

        if (!session) {
          if (DEBUG) console.log('[SessionRefresh] No session found');
          // Não fazer nada se não houver sessão (usuário pode estar em página pública)
          return;
        }

        // Verificar se o token está próximo de expirar (menos de 10 minutos)
        const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
        const timeUntilExpiry = expiresAt - now;
        const TEN_MINUTES = 10 * 60 * 1000;

        if (DEBUG) {
          console.log('[SessionRefresh] Session info:', {
            expiresAt: new Date(expiresAt).toLocaleString(),
            timeUntilExpiry: Math.round(timeUntilExpiry / 1000 / 60) + ' minutes',
          });
        }

        if (timeUntilExpiry < TEN_MINUTES) {
          if (DEBUG) console.log('[SessionRefresh] Token expiring soon, refreshing...');
          
          try {
            const refreshResult = await retryWithBackoff(() => supabase.auth.refreshSession());
            
            if (!refreshResult || !refreshResult.data?.session) {
              throw new Error('Session refresh returned no session');
            }

            if (DEBUG) console.log('[SessionRefresh] Session refreshed proactively');
          } catch (refreshError) {
            console.error('[SessionRefresh] Failed to refresh expiring session:', refreshError);
            
            const isNetworkError = 
              String(refreshError).includes('Failed to fetch') ||
              String(refreshError).includes('ERR_NAME_NOT_RESOLVED') ||
              String(refreshError).includes('Network');

            if (!isNetworkError) {
              toast.error('Sessão expirada', {
                description: 'Por favor, faça login novamente',
                duration: 5000,
              });
              
              await supabase.auth.signOut();
              
              setTimeout(() => {
                window.location.href = '/entrar';
              }, 1000);
            }
          }
        } else {
          if (DEBUG) console.log('[SessionRefresh] Session is valid');
        }
      } catch (error) {
        console.error('[SessionRefresh] Unexpected error:', error);
      } finally {
        isCheckingRef.current = false;
      }
    };

    // Verificar quando a aba volta a ficar visível
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (DEBUG) console.log('[SessionRefresh] Tab became visible, checking session...');
        checkAndRefreshSession();
      }
    };

    // Verificar quando a janela volta a ter foco
    const handleFocus = () => {
      if (DEBUG) console.log('[SessionRefresh] Window focused, checking session...');
      checkAndRefreshSession();
    };

    // Verificar quando há atividade de rede (online/offline)
    const handleOnline = () => {
      if (DEBUG) console.log('[SessionRefresh] Network online, checking session...');
      checkAndRefreshSession();
    };

    // Adicionar listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);

    // Verificação inicial
    checkAndRefreshSession();

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
}
